import { Box, type BoxProps } from '@tokenterminal/ui/Box'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { withScrollBoxShadow } from './ScrollBox.css'

/*
 * ScrollBox is a Box which casts a shadow to top/bottom respectively when content inside the box is scrollable.
 */

const obsRules: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px',
  threshold: 0.1,
}

export const ScrollBox = forwardRef<HTMLElement, BoxProps>(
  ({ children, className, ...props }, ref) => {
    const topRef = useRef<HTMLSpanElement>(null)
    const bottomRef = useRef<HTMLSpanElement>(null)
    const [isTopSeen, setTopShown] = useState(true)
    const [isBottomSeen, setBottomShown] = useState(true)

    useEffect(() => {
      const topObs =
        topRef.current &&
        new IntersectionObserver(([{ isIntersecting }]) => {
          requestAnimationFrame(() => {
            setTopShown(isIntersecting)
          })
        }, obsRules)

      const bottomObs =
        bottomRef.current &&
        new IntersectionObserver(([{ isIntersecting }]) => {
          requestAnimationFrame(() => {
            setBottomShown(isIntersecting)
          })
        }, obsRules)
      if (topObs) {
        topObs.observe(topRef.current)
      }
      if (bottomObs) {
        bottomObs.observe(bottomRef.current)
      }

      return () => {
        if (topObs) {
          topObs.disconnect()
        }
        if (bottomObs) {
          bottomObs.disconnect()
        }
      }
    }, [])

    return (
      <Box
        ref={ref}
        className={[withScrollBoxShadow, className]}
        data-first-shown={isTopSeen}
        data-last-shown={isBottomSeen}
        position="relative" // As prop so it's easy to override in case you have absolutely container in use for example. Relative/absolute is necessary for shadow anyway
        {...props}
      >
        <Box as="span" aria-hidden="true" ref={topRef} />
        {children}
        <Box as="span" aria-hidden="true" ref={bottomRef} />
      </Box>
    )
  },
)

ScrollBox.displayName = 'ScrollBox'
